import React from "react";
import { bind } from "decko";
import HlsJS from "hls.js";

import StreamTimeline from "./StreamTimeline";
import GenericBlackBtn from "./GenericBlackBtn";
import PlaybackRateBtnGroup from "./PlaybackRateBtnGroup";
import Util from "./include/Util";
import Analytics from "./include/Analytics";

export default class PreviewStream extends React.Component {
    constructor (props) {
        super(props);

        this.state = this.createDefaultState();
        this.hotkeys = {
            "space": this.playPause,

            "comma": () => this.shuttleSkip(-0.08),
            "period": () => this.shuttleSkip( 0.08),

            "q": () => this.shuttleSeek = -2.0,
            "w": () => this.shuttleSeek = -1.0,
            "e": () => this.shuttleSeek = -0.65,
            "r": () => this.shuttleSeek = -0.35,
            "t": () => this.shuttleSeek = -0.16,
            "y": () => this.shuttleSeek = -0.08,
            "u": () => this.shuttleSeek = -0.03,
            "i": () => this.shuttleSeek =  0,
            "a": () => this.shuttleSeek =  0.03,
            "s": () => this.shuttleSeek =  0.08,
            "d": () => this.shuttleSeek =  0.16,
            "f": () => this.shuttleSeek =  0.35,
            "g": () => this.shuttleSeek =  0.65,
            "h": () => this.shuttleSeek =  1.0,
            "j": () => this.shuttleSeek =  2.0,

            "1": () => this.seek(this.state.startTime),
            "2": () => this.seek(this.state.eventTime),
            "3": () => this.seek(this.state.endTime),

            "s+1": () => this.markStream("start"),
            "s+2": () => this.markStream("event"),
            "s+3": () => this.markStream("end"),
        };
    }

    createDefaultState (startTime, eventTime, endTime, preroll, postroll) {
        const duration = this.props.playlist.duration / 1000;
        const eventTimeOffset = (this.props.event.videoOffsetMillisec - this.props.playlist.fromTimestamp) / 1000;

        let defaultPrePostRoll = Math.min(20, Math.max(8, 2*Math.floor(duration / 4)));
        this.preroll = preroll || defaultPrePostRoll;
        this.postroll = postroll || defaultPrePostRoll;

        this.shuttleSeek = 0;

        const previewDuration = this.preroll + duration + this.postroll;
        const state = {
            initialPreload: true,
            bufferRanges: [],
            duration: previewDuration,
            expectedDuration: previewDuration,
            progress: 0,
            shuttleSeek: 0,
            startTime: startTime || this.preroll,
            eventTime: eventTime || this.preroll + eventTimeOffset,
            endTime: endTime || this.preroll + duration,
        };

        try {
            Analytics.event("L2_player_load",{
                stream: `${this.props.event.server.name}:${this.props.playlist.id}`,
                details: `${this.props.event.videoOffsetMillisec}:${this.props.playlist.fromTimestamp}_${state.startTime}:${state.eventTime}:${state.endTime}`,
                duration: duration,
            });
        } catch (e) {}

        return state;
    }

    increaseVideoPadding (extraPreroll, extraPostroll) {
        this.destroyPlayer();

        let startTime = this.state.startTime + extraPreroll;
        let eventTime = this.state.eventTime + extraPreroll;
        let endTime = this.state.endTime + extraPreroll;

        this.setState(this.createDefaultState(
            startTime, eventTime, endTime,
            this.preroll + extraPreroll,
            this.postroll + extraPostroll
        ));

        this.loadPlayer();
    }

    componentDidMount () {
        this.loadPlayer();
        this.timelineTick = setInterval(this.updateBuffer, 500);
        this.shuttleTickInterval = setInterval(this.shuttleTick, 200);
        this.props.hotkeyHandler.subscribe(this.hotkeys, this);
    }

    componentWillUnmount () {
        this.destroyPlayer();
        clearInterval(this.timelineTick);
        clearInterval(this.shuttleTickInterval);
        this.props.hotkeyHandler.unsubscribe(this.hotkeys, this);
    }

    getMillisecOffsetTime (time) {
        time = (time - this.preroll) * 1000 + this.props.playlist.fromTimestamp;
        return Math.floor(Math.max(0, time));
    }

    getRoundedMillisecOffsetTime (time) {
        // Rounds to nearest 2sec segment boundary
        return Math.floor(2000 * Math.round(this.getMillisecOffsetTime(time) / 2000));
    }

    @bind
    shuttleSkip (offset) {
        if (!this.videoElement.paused) {
            this.videoElement.pause();
        }
        this.seek(this.videoElement.currentTime + offset);
    }

    @bind
    setShuttleSeek (value) {
        if (value !== 0 && !this.videoElement.paused) {
            this.videoElement.pause();
        }
    }

    @bind
    shuttleTick () {
        if (this.shuttleSeek !== 0) {
            this.shuttleSkip(this.shuttleSeek);
        }
    }

    @bind
    updateProgress () {
        if (!this.state.initialPreload) {
            this.setState({progress: this.videoElement.currentTime});
        }
    }

    @bind
    updateDuration () {
        const duration = this.videoElement.duration;
        this.setState({duration: duration});
    }

    @bind
    markStream (marker) {
        this.moveStreamMarker(marker, this.videoElement.currentTime);
    }

    @bind
    moveStreamMarker (marker, time) {

        let interval = {
            startTime: this.state.startTime,
            eventTime: this.state.eventTime,
            endTime: this.state.endTime,
        };

        switch (marker) {
            case "event":
                if (time < this.state.startTime) {
                    time = this.state.startTime;
                }
                if (time > this.state.endTime) {
                    time = this.state.endTime;
                }
                interval.eventTime = time;
                break;
            case "start":
                if (time > this.state.eventTime) {
                    time = this.state.eventTime;
                }
                interval.startTime = time;
                break;
            case "end":
                if (time < this.state.eventTime) {
                    time = this.state.eventTime;
                }
                interval.endTime = time;
                break;
            default:
                return; /* ignore */
        }

        this.setState(interval);
        this.props.onChangeInterval(
            this.getRoundedMillisecOffsetTime(interval.startTime),
            this.getMillisecOffsetTime(interval.eventTime),
            this.getRoundedMillisecOffsetTime(interval.endTime)
        );
    }

    @bind
    updateBuffer () {

        //Video may have multiple buffer ranges, due to seeking
        const buffers = this.videoElement.buffered;
        let bufferRanges = [];
        for (let i=0; i<buffers.length; ++i) {
            bufferRanges.push({
                start: buffers.start(i),
                end: buffers.end(i),
            });
        }
        if (this.state.initialPreload) {
            if (bufferRanges.some((b) => b.end > this.state.startTime)) {
                this.play();
            }
        }
        this.setState({bufferRanges: bufferRanges});
    }

    loadPlayer () {
        let hlsJsConfig = Util.getHlsJsConfig();
        hlsJsConfig.maxBufferSize = 1024*1024*400;
        hlsJsConfig.maxBufferLength = 120;
        hlsJsConfig.startPosition = this.state.startTime;
        hlsJsConfig.startFragPrefetch = true;
        this.hlsjs = new HlsJS(hlsJsConfig);
        this.hlsjs.attachMedia(this.videoElement);

        this.hlsjs.on(HlsJS.Events.MEDIA_ATTACHED, () => {

            console.log("Video element and hls.js are now bound together!");
            const source = this.props.playlist.getVideoUrl(
                this.props.event.server,
                this.preroll*1000,
                this.postroll*1000
            );
            this.hlsjs.loadSource(source);
            this.hlsjs.on(HlsJS.Events.MANIFEST_PARSED, () => {
                console.log("Manifest parsed we are starting playback");
            });
        });
    }

    destroyPlayer () {
        this.hlsjs.destroy();
    }

    @bind
    seek (time) {
        this.videoElement.currentTime = Math.max(0, time);
    }

    endInitialPreload () {
        if (this.state.initialPreload) {
            this.seek(this.state.startTime);
            this.setState({
                initialPreload: false
            });
        }
    }

    @bind
    play () {
        this.endInitialPreload();
        this.videoElement.play();
    }

    @bind
    playPause () {
        if (this.videoElement.paused) {
            this.play();
        } else {
            this.videoElement.pause();
        }
    }

    render () {
        let video = (
            <video
                onTimeUpdate={this.updateProgress}
                onDurationChange={this.updateDuration}
                onClick={this.playPause}
                ref={(video) => this.videoElement = video} />
        );
        if (!this.videoElement) {
            return <div className="row">{video}</div>;
        }

        return (
            <div className="row">
                {video}
                <div className="click-control-overlay" onClick={this.playPause} />
                <StreamTimeline
                    onSeek={this.seek}
                    onMoveMarker={this.moveStreamMarker}
                    start={this.state.startTime}
                    event={this.state.eventTime}
                    end={this.state.endTime}
                    expectedDuration={this.state.expectedDuration}
                    duration={this.state.duration}
                    progress={this.state.progress}
                    video={this.videoElement}
                    bufferRanges={this.state.bufferRanges} />
                <GenericBlackBtn
                    style={{position: "absolute", left: "10px", bottom: "42px"}}
                    onClick={() => this.increaseVideoPadding(8,0)}>
                    <span className="glyphicon glyphicon-step-backward" />
                </GenericBlackBtn>
                <GenericBlackBtn
                    style={{position: "absolute", right: "10px", bottom: "42px"}}
                    onClick={() => this.increaseVideoPadding(0,8)}>
                    <span className="glyphicon glyphicon-step-forward" />
                </GenericBlackBtn>
                <PlaybackRateBtnGroup
                    hotkeyHandler={this.props.hotkeyHandler}
                    videoElement={this.videoElement}
                    style={{
                        position: "absolute",
                        bottom: "42px",
                        left: "50%",
                        width: "280px",
                        marginLeft: "-140px"
                    }} />
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/js/PreviewStream.js